import React from "react";
import { createRoot } from "react-dom/client";

const WEB_SVG = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cg stroke='%23ffffff10' stroke-width='0.6' fill='none'%3E%3Cline x1='60' y1='0' x2='60' y2='120'/%3E%3Cline x1='0' y1='60' x2='120' y2='60'/%3E%3Cline x1='0' y1='0' x2='120' y2='120'/%3E%3Cline x1='120' y1='0' x2='0' y2='120'/%3E%3Ccircle cx='60' cy='60' r='20'/%3E%3Ccircle cx='60' cy='60' r='40'/%3E%3Ccircle cx='60' cy='60' r='58'/%3E%3C/g%3E%3C/svg%3E")`;

const CARDS = [
  {
    title: "Times Square",
    subtitle: "Manhattan, New York",
    tag: "Iconic Landmark",
    img: "https://images.unsplash.com/photo-1534430480872-3498386e7856?w=400&q=80",
    color: "#E31E24",
  },
  {
    title: "Brooklyn Bridge",
    subtitle: "Brooklyn, New York",
    tag: "Historic Site",
    img: "https://images.unsplash.com/photo-1549318950-4b2b2b4b4b4b?w=400&q=80",
    color: "#003B8E",
  },
  {
    title: "Central Park",
    subtitle: "Midtown Manhattan",
    tag: "Urban Park",
    img: "https://images.unsplash.com/photo-1568515387631-8b650bbcdb90?w=400&q=80",
    color: "#E31E24",
  },
  {
    title: "Empire State",
    subtitle: "Midtown, New York",
    tag: "Skyscraper",
    img: "https://images.unsplash.com/photo-1555109307-f7d9da25c244?w=400&q=80",
    color: "#003B8E",
  },
];

function SpiderIcon() {
  return (
    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-10 h-10">
      <circle cx="32" cy="32" r="30" fill="#E31E24" />
      <path
        d="M32 14 C26 20, 18 24, 10 22 M32 14 C38 20, 46 24, 54 22
           M32 50 C26 44, 18 40, 8 42 M32 50 C38 44, 46 40, 56 42
           M14 32 C20 28, 22 22, 20 14 M14 32 C20 36, 22 42, 20 50
           M50 32 C44 28, 42 22, 44 14 M50 32 C44 36, 42 42, 44 50"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <ellipse cx="32" cy="30" rx="10" ry="13" fill="#1a0000" />
      <circle cx="32" cy="28" r="6" fill="#E31E24" />
    </svg>
  );
}

function WebMask() {
  return (
    <svg
      className="absolute inset-0 w-full h-full opacity-10 pointer-events-none"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <pattern id="web" x="0" y="0" width="120" height="120" patternUnits="userSpaceOnUse">
          <line x1="60" y1="0" x2="60" y2="120" stroke="white" strokeWidth="0.5" />
          <line x1="0" y1="60" x2="120" y2="60" stroke="white" strokeWidth="0.5" />
          <line x1="0" y1="0" x2="120" y2="120" stroke="white" strokeWidth="0.5" />
          <line x1="120" y1="0" x2="0" y2="120" stroke="white" strokeWidth="0.5" />
          <circle cx="60" cy="60" r="20" stroke="white" strokeWidth="0.5" fill="none" />
          <circle cx="60" cy="60" r="40" stroke="white" strokeWidth="0.5" fill="none" />
          <circle cx="60" cy="60" r="58" stroke="white" strokeWidth="0.5" fill="none" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#web)" />
    </svg>
  );
}

function Card({ title, subtitle, tag, img, color }) {
  return (
    <div
      className="relative min-w-[200px] max-w-[200px] w-[60vw] sm:w-[200px] flex-shrink-0 rounded-2xl overflow-hidden shadow-xl select-none"
      style={{ border: `2px solid ${color}33` }}
    >
      <img src={img} alt={title} className="w-full aspect-square object-cover" />
      <div
        className="absolute inset-0"
        style={{
          background: `linear-gradient(to top, ${color}ee 0%, transparent 55%)`,
        }}
      />
      <div className="absolute bottom-0 left-0 right-0 p-3">
        <span
          className="inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full mb-1"
          style={{ backgroundColor: color, color: "white" }}
        >
          {tag}
        </span>
        <div className="text-white font-bold text-sm leading-tight">{title}</div>
        <div className="text-white/60 text-xs mt-0.5">{subtitle}</div>
      </div>
    </div>
  );
}

function App() {
  React.useEffect(() => {
    document.body.style.backgroundColor = "#0d0d0d";
    document.body.style.margin = "0";
    document.body.style.padding = "0";
    document.documentElement.style.backgroundColor = "#0d0d0d";
    return () => {
      document.body.style.backgroundColor = "";
      document.documentElement.style.backgroundColor = "";
    };
  }, []);

  return (
    <div
      className="relative min-h-screen text-white overflow-hidden"
      style={{ backgroundColor: "#0d0d0d" }}
    >
      <WebMask />

      {/* Header */}
      <div className="relative z-10 px-5 pt-6 pb-4">
        <div className="flex items-center gap-3 mb-1">
          <SpiderIcon />
          <div>
            <h1
              className="text-xl font-black tracking-tight leading-none"
              style={{
                background: "linear-gradient(90deg, #E31E24, #ff6b6b)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              SPIDER-MAN
            </h1>
            <p className="text-white/40 text-xs font-medium tracking-widest uppercase">
              Your Friendly Neighborhood
            </p>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2">
          <div
            className="h-1 w-8 rounded-full"
            style={{ backgroundColor: "#E31E24" }}
          />
          <p className="text-white/70 text-sm">
            Explore New York City like a web-slinger
          </p>
        </div>
      </div>

      {/* Card carousel */}
      <div className="relative z-10 px-5 overflow-x-auto pb-2">
        <div className="flex gap-3 items-stretch w-max">
          {CARDS.map((card, i) => (
            <Card key={i} {...card} />
          ))}
        </div>
      </div>

      {/* Footer tagline */}
      <div className="relative z-10 px-5 pt-5 pb-6">
        <div
          className="rounded-2xl p-4 flex items-center gap-3"
          style={{
            background: "linear-gradient(135deg, #E31E2415, #003B8E15)",
            border: "1px solid #E31E2430",
          }}
        >
          <span className="text-2xl">🕸️</span>
          <div>
            <p className="text-white font-semibold text-sm">
              "With great power comes great responsibility."
            </p>
            <p className="text-white/40 text-xs mt-0.5">— Peter Parker</p>
          </div>
        </div>
      </div>
    </div>
  );
}

createRoot(document.getElementById("spiderman-root")).render(<App />);
